import React, { useState, useEffect } from 'react'
import { Alert, Button, Card, Col, Form, Input, Row, Select, Space, Typography, message, Tabs, Divider, Tag, Upload, Radio } from 'antd'
import { ApiOutlined, PlayCircleOutlined, ToolOutlined, DashboardOutlined, NumberOutlined, IdcardOutlined, SwapOutlined, CheckCircleOutlined, InfoCircleOutlined, CloudUploadOutlined, DeleteOutlined, FilePdfOutlined, PictureOutlined, FileOutlined } from '@ant-design/icons'
import { profilingAPI, reviewAPI, systemAPI, dashboardAPI, OcrDocument } from '../services/api'
import SimulationIdGenerator from '../components/SimulationIdGenerator'
import AutomatedFlow from '../components/AutomatedFlow'

const { Title, Text } = Typography
const { TextArea } = Input
const { TabPane } = Tabs

type JsonState = {
  health?: unknown
  ready?: unknown
  live?: unknown
  trigger?: unknown
  analyze?: unknown
  getResult?: unknown
  getStatus?: unknown
  pending?: unknown
  submitReview?: unknown
}

// ── Shared HEIC helper (mirrors OcrAnalysis) ─────────────────────────────────

const toBase64 = (file: File): Promise<string> =>
  new Promise((res, rej) => {
    const r = new FileReader()
    r.onload = () => res((r.result as string).split(',')[1])
    r.onerror = rej
    r.readAsDataURL(file)
  })

const isHeic = (f: File) => f.type === 'image/heic' || f.type === 'image/heif' || /\.(heic|heif)$/i.test(f.name)

async function fileToDoc(file: File, filename?: string): Promise<OcrDocument> {
  if (isHeic(file)) {
    try {
      const bitmap = await createImageBitmap(file)
      const canvas = document.createElement('canvas')
      canvas.width = bitmap.width; canvas.height = bitmap.height
      canvas.getContext('2d')!.drawImage(bitmap, 0, 0)
      const blob = await new Promise<Blob>((res, rej) =>
        canvas.toBlob(b => b ? res(b) : rej(new Error('toBlob failed')), 'image/jpeg', 0.92))
      return { filename: filename ?? file.name, base64: await toBase64(new File([blob], file.name)), content_type: 'image/jpeg' }
    } catch { /* fallthrough */ }
  }
  return { filename: filename ?? file.name, base64: await toBase64(file), content_type: file.type || 'image/jpeg' }
}

const fileIcon = (file: File) => {
  if (file.type.includes('pdf')) return <FilePdfOutlined style={{ color: '#ff4d4f' }} />
  if (file.type.includes('image')) return <PictureOutlined style={{ color: '#1890ff' }} />
  return <FileOutlined />
}

// ── Mini file slot (same look as OcrAnalysis) ─────────────────────────────────

interface MiniSlotProps { label: string; hint: string; file: File | null; onChange: (f: File | null) => void }
const MiniSlot: React.FC<MiniSlotProps> = ({ label, hint, file, onChange }) => (
  <div style={{ marginBottom: 10 }}>
    <Text strong style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>{label}</Text>
    {file ? (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#f6ffed', border: '1px solid #b7eb8f', borderRadius: 6, padding: '5px 10px' }}>
        {fileIcon(file)}
        <Text style={{ flex: 1, fontSize: 12 }} ellipsis>{file.name}</Text>
        <Button type="text" size="small" danger icon={<DeleteOutlined />} onClick={() => onChange(null)} />
      </div>
    ) : (
      <Upload accept=".pdf,.jpg,.jpeg,.png,.heic,.heif" maxCount={1} beforeUpload={f => { onChange(f); return false }} showUploadList={false}>
        <div style={{ border: '1px dashed #d9d9d9', borderRadius: 6, padding: '5px 10px', cursor: 'pointer', background: '#fafafa', display: 'flex', alignItems: 'center', gap: 6 }}>
          <CloudUploadOutlined style={{ color: '#8c8c8c' }} />
          <Text style={{ fontSize: 12, color: '#595959' }}>{hint}</Text>
        </div>
      </Upload>
    )}
  </div>
)

// ── AutomatedFlowWithDocs: convierte archivos → OcrDocument[] y pasa a AutomatedFlow ──

interface AutomatedFlowWithDocsProps {
  docFiles: { cc: File | null; reverso: File | null; voucher: File | null }
  cedula: string
  onComplete?: (results: any) => void
}

const AutomatedFlowWithDocs: React.FC<AutomatedFlowWithDocsProps> = ({ docFiles, cedula, onComplete }) => {
  const [docs, setDocs] = useState<OcrDocument[]>([])
  const [converting, setConverting] = useState(false)

  // Reconvertir archivos cada vez que cambien
  useEffect(() => {
    const hasFiles = docFiles.cc || docFiles.reverso || docFiles.voucher
    if (!hasFiles) { setDocs([]); return }
    setConverting(true)
    ;(async () => {
      const result: OcrDocument[] = []
      if (docFiles.cc)
        result.push(await fileToDoc(docFiles.cc, docFiles.cc.name))
      if (docFiles.reverso) {
        const name = docFiles.reverso.name.toUpperCase().includes('REVERSO')
          ? docFiles.reverso.name
          : `CC REVERSO.${docFiles.reverso.name.split('.').pop()}`
        result.push(await fileToDoc(docFiles.reverso, name))
      }
      if (docFiles.voucher)
        result.push(await fileToDoc(docFiles.voucher))
      setDocs(result)
      setConverting(false)
    })()
  }, [docFiles])

  if (!docFiles.cc && !docFiles.voucher) {
    return (
      <Card title="Flujo Automatizado">
        <Alert
          message="Sube los documentos"
          description="Carga al menos la cédula (frente) o el desprendible en el panel izquierdo para ejecutar el flujo."
          type="info"
          showIcon
        />
      </Card>
    )
  }

  if (converting) {
    return <Card title="Flujo Automatizado"><Text type="secondary">Preparando archivos…</Text></Card>
  }

  return (
    <AutomatedFlow
      simulationId=""
      documents={docs}
      cedula={cedula || undefined}
      onComplete={onComplete}
    />
  )
}

// ─────────────────────────────────────────────────────────────────────────────

const ApiOperations: React.FC = () => {
  const [loading, setLoading] = useState<Record<string, boolean>>({})
  const [result, setResult] = useState<JsonState>({})
  const [currentSimulationId, setCurrentSimulationId] = useState<string>('')
  const [recentResults, setRecentResults] = useState<Array<{id: string, timestamp: string, result: any}>>([])

  // Analyze mode: 'id' (simulation ID) or 'docs' (document upload)
  const [analyzeMode, setAnalyzeMode] = useState<'id' | 'docs'>('id')
  const [docFiles, setDocFiles] = useState<{ cc: File | null; reverso: File | null; voucher: File | null }>({ cc: null, reverso: null, voucher: null })
  const [docCedula, setDocCedula] = useState('')

  const run = async (key: keyof JsonState, fn: () => Promise<unknown>) => {
    try {
      setLoading((prev) => ({ ...prev, [key]: true }))
      const data = await fn()
      setResult((prev) => ({ ...prev, [key]: data }))
      message.success(`Endpoint ${key} ejecutado correctamente`)
      
      // Guardar resultado en historial
      setRecentResults(prev => [{
        id: key,
        timestamp: new Date().toLocaleString(),
        result: data
      }, ...prev.slice(0, 9)]) // Mantener últimos 10
      
    } catch (error: any) {
      const detail = error?.response?.data || error?.message || 'Error desconocido'
      setResult((prev) => ({ ...prev, [key]: detail }))
      message.error(`Falló endpoint ${key}: ${detail.detail || detail}`)
    } finally {
      setLoading((prev) => ({ ...prev, [key]: false }))
    }
  }

  const refreshDashboard = async () => {
    try {
      setLoading(prev => ({ ...prev, refreshDashboard: true }))
      await dashboardAPI.getSummary()
      message.success('Dashboard actualizado')
    } catch (error) {
      message.error('Error al actualizar dashboard')
    } finally {
      setLoading(prev => ({ ...prev, refreshDashboard: false }))
    }
  }

  const handleAutomatedFlowComplete = (results: any) => {
    message.success('Flujo automatizado completado')
    // Auto-refresh del dashboard
    refreshDashboard()
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Title level={2} style={{ margin: 0 }}>Operaciones API</Title>
        <Button 
          icon={<DashboardOutlined />}
          onClick={refreshDashboard}
          loading={loading.refreshDashboard}
        >
          Actualizar Dashboard
        </Button>
      </div>

      <Tabs defaultActiveKey="automated" type="card">
        
        {/* TAB 1: Flujo Automatizado */}
        <TabPane tab={<span><PlayCircleOutlined />Flujo Automatizado</span>} key="automated">
          <Row gutter={[16, 16]}>

            {/* ── Panel izquierdo: fuente de datos ── */}
            <Col xs={24} lg={12}>
              <Card
                title="Fuente de datos"
                extra={
                  <Radio.Group
                    size="small"
                    value={analyzeMode}
                    onChange={e => {
                      setAnalyzeMode(e.target.value)
                      setCurrentSimulationId('')
                      setDocFiles({ cc: null, reverso: null, voucher: null })
                      setDocCedula('')
                    }}
                    optionType="button"
                    buttonStyle="solid"
                  >
                    <Radio.Button value="id">Por ID</Radio.Button>
                    <Radio.Button value="docs">Por Documentos</Radio.Button>
                  </Radio.Group>
                }
              >
                {analyzeMode === 'id' ? (
                  <SimulationIdGenerator
                    onIdGenerated={setCurrentSimulationId}
                    title=""
                  />
                ) : (
                  <div>
                    <Alert
                      type="info"
                      showIcon
                      style={{ marginBottom: 12, fontSize: 12 }}
                      message="Sube cédula y desprendible para ejecutar el análisis sin ID SEAS"
                    />
                    <MiniSlot
                      label="Cédula (Frente) — requerida"
                      hint="PDF · JPG · PNG · HEIC"
                      file={docFiles.cc}
                      onChange={f => setDocFiles(prev => ({ ...prev, cc: f }))}
                    />
                    <MiniSlot
                      label="Cédula (Reverso) — opcional"
                      hint="PDF · JPG · PNG · HEIC"
                      file={docFiles.reverso}
                      onChange={f => setDocFiles(prev => ({ ...prev, reverso: f }))}
                    />
                    <MiniSlot
                      label="Desprendible / Comprobante — requerido"
                      hint="Colpensiones · FOPEP · SED · CREMIL · PDF · imagen"
                      file={docFiles.voucher}
                      onChange={f => setDocFiles(prev => ({ ...prev, voucher: f }))}
                    />
                    <div style={{ marginTop: 8 }}>
                      <Text style={{ fontSize: 12 }}>Cédula (opcional, para validación cruzada)</Text>
                      <Input
                        size="small"
                        placeholder="Número de cédula"
                        value={docCedula}
                        onChange={e => setDocCedula(e.target.value)}
                        style={{ marginTop: 4 }}
                      />
                    </div>
                  </div>
                )}
              </Card>
            </Col>

            {/* ── Panel derecho: AutomatedFlow ── */}
            <Col xs={24} lg={12}>
              {analyzeMode === 'id' ? (
                currentSimulationId ? (
                  <AutomatedFlow
                    simulationId={currentSimulationId}
                    onComplete={handleAutomatedFlowComplete}
                  />
                ) : (
                  <Card title="Flujo Automatizado">
                    <Alert
                      message="Genera un Simulation ID"
                      description="Primero genera o selecciona un Simulation ID en el panel izquierdo."
                      type="info"
                      showIcon
                    />
                  </Card>
                )
              ) : (
                <AutomatedFlowWithDocs
                  docFiles={docFiles}
                  cedula={docCedula}
                  onComplete={handleAutomatedFlowComplete}
                />
              )}
            </Col>
          </Row>

          {recentResults.length > 0 && (
            <Card title="Resultados Recientes" style={{ marginTop: 16 }}>
              <Row gutter={[8, 8]}>
                {recentResults.slice(0, 5).map((result, index) => (
                  <Col xs={24} sm={12} lg={8} xl={4} key={index}>
                    <Card size="small" style={{ fontSize: '12px' }}>
                      <Text strong>{result.id}</Text>
                      <br />
                      <Text type="secondary">{result.timestamp}</Text>
                    </Card>
                  </Col>
                ))}
              </Row>
            </Card>
          )}
        </TabPane>

        {/* TAB 2: Endpoints Manuales */}
        <TabPane tab={<span><ToolOutlined />Endpoints Manuales</span>} key="manual">
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
            message="Testing manual de endpoints individuales"
            description="Usa estos formularios para probar endpoints específicos cuando necesites control detallado."
          />

          <Row gutter={[16, 16]}>
            {/* Health Endpoints */}
            <Col xs={24} xl={12}>
              <Card title="Health Endpoints">
                <Space wrap>
                  <Button loading={loading.health} onClick={() => run('health', () => systemAPI.health())}>GET /health</Button>
                  <Button loading={loading.ready} onClick={() => run('ready', () => systemAPI.ready())}>GET /ready</Button>
                  <Button loading={loading.live} onClick={() => run('live', () => systemAPI.live())}>GET /live</Button>
                </Space>
                {result.health && (
                  <pre style={{ marginTop: 12, maxHeight: 220, overflow: 'auto' }}>{JSON.stringify(result.health, null, 2)}</pre>
                )}
                {result.ready && (
                  <pre style={{ marginTop: 12, maxHeight: 220, overflow: 'auto' }}>{JSON.stringify(result.ready, null, 2)}</pre>
                )}
                {result.live && (
                  <pre style={{ marginTop: 12, maxHeight: 220, overflow: 'auto' }}>{JSON.stringify(result.live, null, 2)}</pre>
                )}
              </Card>
            </Col>

            {/* Profiling Endpoints */}
            <Col xs={24} xl={12}>
              <Card title="Profiling: Trigger y Analyze">
                <Form
                  layout="vertical"
                  onFinish={(values) =>
                    run('trigger', () =>
                      profilingAPI.trigger({
                        simulation_id: values.simulation_id || currentSimulationId,
                        trigger_source: values.trigger_source || 'frontend_manual',
                        priority: values.priority || 'normal',
                      }),
                    )
                  }
                  initialValues={{ 
                    simulation_id: currentSimulationId,
                    trigger_source: 'frontend_manual', 
                    priority: 'normal' 
                  }}
                >
                  <Form.Item name="simulation_id" label="simulation_id" rules={[{ required: true }]}>
                    <Input placeholder={currentSimulationId || "745437"} />
                  </Form.Item>
                  <Form.Item name="trigger_source" label="trigger_source">
                    <Input />
                  </Form.Item>
                  <Form.Item name="priority" label="priority">
                    <Select options={[{ value: 'normal' }, { value: 'high' }]} />
                  </Form.Item>
                  <Button htmlType="submit" loading={loading.trigger} type="primary">POST /v1/profiling/trigger</Button>
                </Form>

                <Divider />

                {/* Profiling: Analyze — ID o Documentos */}
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                    <Text strong style={{ fontSize: 13 }}>Profiling: Analyze</Text>
                    <Radio.Group
                      size="small"
                      value={analyzeMode}
                      onChange={e => setAnalyzeMode(e.target.value)}
                      optionType="button"
                      buttonStyle="solid"
                    >
                      <Radio.Button value="id">Por ID</Radio.Button>
                      <Radio.Button value="docs">Por Documentos</Radio.Button>
                    </Radio.Group>
                  </div>

                  {analyzeMode === 'id' ? (
                    <Form
                      layout="vertical"
                      onFinish={(values) =>
                        run('analyze', () =>
                          profilingAPI.analyze({
                            simulation_id: values.simulation_id || currentSimulationId,
                            customer_type: values.customer_type,
                            force_refresh: Boolean(values.force_refresh),
                          }),
                        )
                      }
                      initialValues={{ simulation_id: currentSimulationId }}
                    >
                      <Form.Item name="simulation_id" label="simulation_id" rules={[{ required: true }]}>
                        <Input placeholder={currentSimulationId || "745437"} />
                      </Form.Item>
                      <Form.Item name="customer_type" label="customer_type">
                        <Select allowClear options={[{ value: 'pensionado' }, { value: 'docente' }]} />
                      </Form.Item>
                      <Form.Item name="force_refresh" label="force_refresh">
                        <Select allowClear options={[{ value: true, label: 'true' }, { value: false, label: 'false' }]} />
                      </Form.Item>
                      <Button htmlType="submit" loading={loading.analyze}>POST /v1/profiling/analyze</Button>
                    </Form>
                  ) : (
                    <div>
                      <Alert
                        type="info"
                        showIcon
                        style={{ marginBottom: 10, fontSize: 12 }}
                        message="Análisis por documentos OCR"
                        description="El motor extrae identidad e ingresos de los documentos y ejecuta el flujo de validación. La verificación de antecedentes requiere un ID SEAS."
                      />
                      <MiniSlot
                        label="Cédula (Frente) — requerida"
                        hint="CC frente · PDF / JPG / PNG / HEIC"
                        file={docFiles.cc}
                        onChange={f => setDocFiles(prev => ({ ...prev, cc: f }))}
                      />
                      <MiniSlot
                        label="Cédula (Reverso) — opcional"
                        hint="CC reverso · PDF / JPG / PNG / HEIC"
                        file={docFiles.reverso}
                        onChange={f => setDocFiles(prev => ({ ...prev, reverso: f }))}
                      />
                      <MiniSlot
                        label="Desprendible / Comprobante — requerido"
                        hint="Colpensiones, FOPEP, SED, CREMIL… · PDF / JPG / PNG / HEIC"
                        file={docFiles.voucher}
                        onChange={f => setDocFiles(prev => ({ ...prev, voucher: f }))}
                      />
                      <div style={{ marginBottom: 8 }}>
                        <Text style={{ fontSize: 12 }}>Cédula (opcional, para validación cruzada)</Text>
                        <Input
                          size="small"
                          placeholder="Número de cédula"
                          value={docCedula}
                          onChange={e => setDocCedula(e.target.value)}
                          style={{ marginTop: 4 }}
                        />
                      </div>
                      <Button
                        loading={loading.analyze}
                        disabled={!docFiles.cc && !docFiles.voucher}
                        onClick={async () => {
                          const documents: OcrDocument[] = []
                          if (docFiles.cc) documents.push(await fileToDoc(docFiles.cc, docFiles.cc.name))
                          if (docFiles.reverso) {
                            const name = docFiles.reverso.name.toUpperCase().includes('REVERSO')
                              ? docFiles.reverso.name
                              : `CC REVERSO.${docFiles.reverso.name.split('.').pop()}`
                            documents.push(await fileToDoc(docFiles.reverso, name))
                          }
                          if (docFiles.voucher) documents.push(await fileToDoc(docFiles.voucher))
                          run('analyze', () =>
                            profilingAPI.analyzeWithDocs({
                              documents,
                              cedula: docCedula.trim() || undefined,
                            })
                          )
                        }}
                      >
                        POST /v1/profiling/analyze (OCR)
                      </Button>
                    </div>
                  )}
                </div>

                {result.trigger && (
                  <details style={{ marginTop: 12 }}>
                    <summary>Ver resultado Trigger</summary>
                    <pre style={{ maxHeight: 200, overflow: 'auto' }}>{JSON.stringify(result.trigger, null, 2)}</pre>
                  </details>
                )}
                {result.analyze && (
                  <details style={{ marginTop: 12 }}>
                    <summary>Ver resultado Analyze</summary>
                    <pre style={{ maxHeight: 200, overflow: 'auto' }}>{JSON.stringify(result.analyze, null, 2)}</pre>
                  </details>
                )}
              </Card>
            </Col>

            {/* Result & Status */}
            <Col xs={24} xl={12}>
              <Card title="Profiling: Result y Status">
                <Form
                  layout="vertical"
                  onFinish={(values) => run('getResult', () => profilingAPI.getResult(values.profiling_id))}
                >
                  <Form.Item name="profiling_id" label="profiling_id (UUID)" rules={[{ required: true }]}>
                    <Input placeholder="Usar UUID del trigger/analyze" />
                  </Form.Item>
                  <Button htmlType="submit" loading={loading.getResult}>GET /v1/profiling/result</Button>
                </Form>

                <Divider />

                <Form
                  layout="vertical"
                  onFinish={(values) => run('getStatus', () => profilingAPI.getStatus(values.profiling_id))}
                >
                  <Form.Item name="profiling_id" label="profiling_id (UUID)" rules={[{ required: true }]}>
                    <Input placeholder="Usar UUID del trigger/analyze" />
                  </Form.Item>
                  <Button htmlType="submit" loading={loading.getStatus}>GET /v1/profiling/status</Button>
                </Form>

                {result.getResult && (
                  <details style={{ marginTop: 12 }}>
                    <summary>Ver Result</summary>
                    <pre style={{ maxHeight: 200, overflow: 'auto' }}>{JSON.stringify(result.getResult, null, 2)}</pre>
                  </details>
                )}
                {result.getStatus && (
                  <details style={{ marginTop: 12 }}>
                    <summary>Ver Status</summary>
                    <pre style={{ maxHeight: 200, overflow: 'auto' }}>{JSON.stringify(result.getStatus, null, 2)}</pre>
                  </details>
                )}
              </Card>
            </Col>

            {/* Manual Review */}
            <Col xs={24} xl={12}>
              <Card title="Manual Review">
                <Space style={{ marginBottom: 16 }}>
                  <Button loading={loading.pending} onClick={() => run('pending', () => reviewAPI.getPending(50, 0))}>
                    GET /v1/review/pending
                  </Button>
                </Space>

                <Form
                  layout="vertical"
                  onFinish={(values) =>
                    run('submitReview', () =>
                      reviewAPI.submitReview(values.profiling_id, {
                        analyst_id: values.analyst_id,
                        analyst_name: values.analyst_name,
                        review_notes: values.review_notes,
                        final_decision: values.final_decision,
                      }),
                    )
                  }
                  initialValues={{ 
                    final_decision: 'APPROVED',
                    analyst_id: 'analyst-001',
                    analyst_name: 'Analista QA'
                  }}
                >
                  <Form.Item name="profiling_id" label="profiling_id (UUID)" rules={[{ required: true }]}>
                    <Input placeholder="UUID del profiling request" />
                  </Form.Item>
                  <Form.Item name="analyst_id" label="analyst_id" rules={[{ required: true }]}>
                    <Input />
                  </Form.Item>
                  <Form.Item name="analyst_name" label="analyst_name" rules={[{ required: true }]}>
                    <Input />
                  </Form.Item>
                  <Form.Item name="final_decision" label="final_decision" rules={[{ required: true }]}>
                    <Select options={[
                      { value: 'APPROVED', label: 'APPROVED' },
                      { value: 'REJECTED', label: 'REJECTED' },
                      { value: 'PENDING_REVIEW', label: 'PENDING_REVIEW' },
                    ]} />
                  </Form.Item>
                  <Form.Item name="review_notes" label="review_notes" rules={[{ required: true }]}>
                    <TextArea rows={3} placeholder="Notas del analista..." />
                  </Form.Item>
                  <Button htmlType="submit" loading={loading.submitReview}>PUT /v1/review/submit</Button>
                </Form>

                {result.pending && (
                  <details style={{ marginTop: 12 }}>
                    <summary>Ver Pending Reviews</summary>
                    <pre style={{ maxHeight: 200, overflow: 'auto' }}>{JSON.stringify(result.pending, null, 2)}</pre>
                  </details>
                )}
                {result.submitReview && (
                  <details style={{ marginTop: 12 }}>
                    <summary>Ver Submit Review</summary>
                    <pre style={{ maxHeight: 200, overflow: 'auto' }}>{JSON.stringify(result.submitReview, null, 2)}</pre>
                  </details>
                )}
              </Card>
            </Col>
          </Row>
        </TabPane>

        {/* TAB 3: Información del Sistema */}
        <TabPane tab={<span><ApiOutlined />Info Sistema</span>} key="system">

          {/* Flujo de detección automática */}
          <Card
            title={<><SwapOutlined style={{ marginRight: 6, color: '#722ed1' }} />Detección Automática: Simulation ID o Cédula</>}
            style={{ marginBottom: 16 }}
          >
            <Row gutter={[16, 0]} align="middle">
              <Col xs={24} md={16}>
                <Text>
                  El sistema acepta tanto <strong>Simulation IDs</strong> (números de solicitud SEAS) como
                  <strong> números de cédula</strong> sin que debas indicar cuál es. Al ejecutar el flujo,
                  primero intenta con el ID como simulation_id; si SEAS no lo encuentra, lo reintenta
                  automáticamente como cédula.
                </Text>
                <div style={{ marginTop: 12 }}>
                  <Space wrap>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                      <Tag icon={<NumberOutlined />} color="blue" style={{ margin: 0 }}>Simulation ID</Tag>
                      <Text style={{ fontSize: 11 }} type="secondary">ej: 745437</Text>
                    </div>
                    <Text type="secondary">→ si falla →</Text>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                      <Tag icon={<IdcardOutlined />} color="purple" style={{ margin: 0 }}>Cédula (fallback)</Tag>
                      <Text style={{ fontSize: 11 }} type="secondary">ej: 10590499</Text>
                    </div>
                    <Text type="secondary">→</Text>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                      <Tag icon={<CheckCircleOutlined />} color="green" style={{ margin: 0 }}>Datos SEAS</Tag>
                      <Text style={{ fontSize: 11 }} type="secondary">perfil completo</Text>
                    </div>
                  </Space>
                </div>
              </Col>
              <Col xs={24} md={8}>
                <Alert
                  type="info"
                  icon={<InfoCircleOutlined />}
                  showIcon
                  message="¿Qué verás en el flujo?"
                  description={
                    <ul style={{ margin: '4px 0 0', paddingLeft: 16, fontSize: 12 }}>
                      <li>Paso 2 muestra qué método se está intentando</li>
                      <li>Si hay fallback, indica el motivo exacto</li>
                      <li>Al terminar, una etiqueta muestra cómo se resolvió</li>
                    </ul>
                  }
                />
              </Col>
            </Row>
          </Card>

          <Row gutter={[16, 16]}>
            {/* Endpoints */}
            <Col xs={24} md={12}>
              <Card title="Endpoints Disponibles">
                <Space direction="vertical" style={{ width: '100%' }} size={6}>
                  <div>
                    <Text code>GET /health</Text>
                    <Text type="secondary" style={{ marginLeft: 8 }}>Estado del sistema y sus dependencias</Text>
                  </div>
                  <div>
                    <Text code>POST /v1/profiling/trigger</Text>
                    <Text type="secondary" style={{ marginLeft: 8 }}>Registrar solicitud y encolar análisis</Text>
                  </div>
                  <div>
                    <Text code>POST /v1/profiling/analyze</Text>
                    <Text type="secondary" style={{ marginLeft: 8 }}>Análisis completo y sincrónico — acepta <Text code style={{ fontSize: 11 }}>cedula</Text> como campo opcional</Text>
                  </div>
                  <div>
                    <Text code>GET /v1/profiling/{'{id}'}</Text>
                    <Text type="secondary" style={{ marginLeft: 8 }}>Resultado por UUID de profiling</Text>
                  </div>
                  <div>
                    <Text code>GET /v1/profiling/status/{'{id}'}</Text>
                    <Text type="secondary" style={{ marginLeft: 8 }}>Estado de procesamiento</Text>
                  </div>
                  <div>
                    <Text code>GET /v1/review/pending</Text>
                    <Text type="secondary" style={{ marginLeft: 8 }}>Cola de revisión manual</Text>
                  </div>
                  <div>
                    <Text code>PUT /v1/review/{'{id}'}</Text>
                    <Text type="secondary" style={{ marginLeft: 8 }}>Enviar decisión del analista</Text>
                  </div>
                  <div>
                    <Text code>POST /v1/ocr/analyze</Text>
                    <Text type="secondary" style={{ marginLeft: 8 }}>Extracción OCR de cédulas y comprobantes</Text>
                  </div>
                </Space>
              </Card>
            </Col>

            {/* IDs de prueba */}
            <Col xs={24} md={12}>
              <Card title="IDs de Prueba — Endpoint Producción">
                <Space direction="vertical" style={{ width: '100%' }} size={10}>
                  <Alert
                    type="success"
                    showIcon
                    message="Endpoint actualizado a producción"
                    description="Ahora usa api.kredit.com.co — acepta simulation_id y cédula."
                    style={{ marginBottom: 4 }}
                  />

                  <div>
                    <Text strong>Como Simulation ID</Text>
                    <div style={{ marginTop: 6 }}>
                      {[
                        { id: '745437', desc: 'COLFONDOS · Pensionado · Score 751', tag: 'APPROVED' },
                        { id: '745148', desc: 'SED BOGOTÁ · Pensionado · Score 668', tag: 'APPROVED' },
                        { id: '745147', desc: 'COLPENSIONES · Propiedad · Score 471', tag: 'APPROVED' },
                      ].map(({ id, desc, tag }) => (
                        <div key={id} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                          <Tag icon={<NumberOutlined />} color="blue" style={{ margin: 0, fontFamily: 'monospace' }}>{id}</Tag>
                          <Text style={{ fontSize: 12 }}>{desc}</Text>
                          <Tag color="green" style={{ margin: 0, fontSize: 10 }}>{tag}</Tag>
                        </div>
                      ))}
                    </div>
                  </div>

                  <Divider style={{ margin: '4px 0' }} />

                  <div>
                    <Text strong>Como Cédula</Text>
                    <Text type="secondary" style={{ marginLeft: 8, fontSize: 12 }}>
                      Cualquier cédula de los 152 casos en <Text code style={{ fontSize: 11 }}>data/INSUMOS CASOS MOTOR/</Text>
                    </Text>
                    <div style={{ marginTop: 6 }}>
                      {[
                        { id: '10590499', desc: 'Caso de prueba — pensionado' },
                        { id: '32682648', desc: 'Caso de prueba — COLPENSIONES' },
                        { id: '12597334', desc: 'Caso de prueba — FIDUPREVISORA' },
                      ].map(({ id, desc }) => (
                        <div key={id} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                          <Tag icon={<IdcardOutlined />} color="purple" style={{ margin: 0, fontFamily: 'monospace' }}>{id}</Tag>
                          <Text style={{ fontSize: 12 }} type="secondary">{desc}</Text>
                        </div>
                      ))}
                    </div>
                  </div>

                  <Alert
                    type="info"
                    showIcon
                    message="El sistema intenta ambos automáticamente"
                    description="No tienes que saber si un número es simulation_id o cédula. El fallback lo resuelve."
                    style={{ marginTop: 4 }}
                  />
                </Space>
              </Card>
            </Col>
          </Row>
        </TabPane>

      </Tabs>
    </div>
  )
}

export default ApiOperations
